import type { DisplayDateMode, TypeDisplayLanguage, TypeDisplayTheme } from '@/api/types/map-schema-types';
import type { IAppState } from '@/core/stores/store-interface-and-intial-values/app-state';
import { AbstractEventProcessor } from '@/api/event-processors/abstract-event-processor';
import type { NotificationDetailsType } from '@/core/components';
import { formatError } from '@/core/exceptions/core-exceptions';
import type { TypeHTMLElement } from '@/core/types/global-types';
import { DateMgt, type TimeIANA, type TypeDisplayDateDefaults } from '@/core/utils/date-mgt';
import { createGuideObject } from '@/core/utils/utilities';
import { MapEventProcessor } from './map-event-processor';
import type { SnackbarType } from '@/core/utils/notifications';
import { logger } from '@/core/utils/logger';

// GV Important: See notes in header of MapEventProcessor file for information on the paradigm to apply when working with UIEventProcessor vs UIState

export class AppEventProcessor extends AbstractEventProcessor {
  // Static functions for Typescript files to access store actions
  // **********************************************************
  // GV Typescript MUST always use the defined store actions below to modify store - NEVER use setState!
  // GV Some action does state modifications AND map actions.
  // GV ALWAYS use map event processor when an action modify store and IS NOT trap by map state event handler

  // #region
  /**
   * Shortcut to get the App state for a given map id
   * @param {string} mapId - The mapId
   * @returns {IAppState} The App state.
   */
  protected static getAppState(mapId: string): IAppState {
    // Return the app state
    return super.getState(mapId).appState;
  }

  /**
   * Shortcut to get the App state for a given map id
   * @param {string} mapId - The mapId
   * @returns {IAppState} The App state.
   */
  protected static async getAppStateAsync(mapId: string): Promise<IAppState> {
    // Return the app state
    return (await super.getStateAsync(mapId)).appState;
  }

  /**
   * Shortcut to get the display language for a given map id
   * @param {string} mapId - The mapId
   * @returns {TypeDisplayLanguage} The display language.
   */
  static getDisplayLanguage(mapId: string): TypeDisplayLanguage {
    return this.getAppState(mapId).displayLanguage;
  }

  /**
   * Shortcut to get the display date mode for a given map id
   * @param {string} mapId - The mapId
   * @returns {DisplayDateMode} The display date mode.
   */
  static getDisplayDateMode(mapId: string): DisplayDateMode {
    return this.getAppState(mapId).displayDateMode;
  }

  /**
   * Shortcut to get the display date mode for a given map id
   * @param {string} mapId - The mapId
   * @returns {TypeDisplayDateDefaults} The display date mode.
   */
  static getDisplayDateFormatDefault(mapId: string): TypeDisplayDateDefaults {
    return DateMgt.getDisplayDateDefaults(this.getDisplayDateMode(mapId));
  }

  /**
   * Shortcut to get the display date timezone for a given map id
   * @param {string} mapId - The mapId
   * @returns {TimeIANA} The display date timezone.
   */
  static getDisplayDateTimezone(mapId: string): TimeIANA {
    return this.getAppState(mapId).displayDateTimezone;
  }

  /**
   * Shortcut to get the display theme for a given map id
   * @param {string} mapId - The mapId
   * @returns {TypeDisplayTheme} The display theme.
   */
  static getDisplayTheme(mapId: string): TypeDisplayTheme {
    return this.getAppState(mapId).displayTheme;
  }

  /**
   * Shortcut to get the GeoView HTML Element
   * @param {string} mapId - The mapId
   * @returns {HTMLElement} The GeoView HTML Element
   */
  static getGeoviewHTMLElement(mapId: string): HTMLElement {
    return this.getAppState(mapId).geoviewHTMLElement;
  }

  /**
   * Shortcut to get if unsymbolized features should be shown
   * @param {string} mapId - The mapId
   * @returns {boolean} Whether unsymbolized features should be shown
   */
  static getShowUnsymbolizedFeatures(mapId: string): boolean {
    return this.getAppState(mapId).showUnsymbolizedFeatures;
  }

  /**
   * Shortcut to get if the bounding box of the highlighted layer should be shown
   * @param {string} mapId - The mapId
   * @returns {boolean} Whether to show the bounding box of the highlighted layer
   */
  static getShowLayerHighlightLayerBbox(mapId: string): boolean {
    return this.getAppState(mapId).showLayerHighlightLayerBbox;
  }

  /**
   * Adds a snackbar message (optional add to notification).
   * @param {SnackbarType} type - The type of message.
   * @param {string} messageKey - The message key.
   * @param {unknown[]} messageParams - Optional param to replace in the string if it is a key
   * @param {boolean} notification - True if we add the message to notification panel (default false)
   */
  static addMessage(mapId: string, type: SnackbarType, messageKey: string, messageParams?: unknown[], notification: boolean = false): void {
    switch (type) {
      case 'info':
        MapEventProcessor.getMapViewer(mapId).notifications.showMessage(messageKey, messageParams, notification);
        break;
      case 'success':
        MapEventProcessor.getMapViewer(mapId).notifications.showSuccess(messageKey, messageParams, notification);
        break;
      case 'warning':
        MapEventProcessor.getMapViewer(mapId).notifications.showWarning(messageKey, messageParams, notification);
        break;
      case 'error':
        MapEventProcessor.getMapViewer(mapId).notifications.showError(messageKey, messageParams, notification);
        break;
      default:
        break;
    }
  }

  static async addNotification(mapId: string, notif: NotificationDetailsType): Promise<void> {
    // Because notification is called before map is created, we use the async
    // version of getAppStateAsync
    const appState = await this.getAppStateAsync(mapId);
    const curNotifications = appState.notifications;
    // if the notification already exist, we increment the count
    const existingNotif = curNotifications.find(
      (item) => item.message === notif.message && item.notificationType === notif.notificationType
    );

    if (!existingNotif) {
      curNotifications.push({ key: notif.key, notificationType: notif.notificationType, message: notif.message, count: 1 });
    } else {
      existingNotif.count += 1;
    }

    this.getAppState(mapId).setterActions.setNotifications(curNotifications);
  }

  static removeNotification(mapId: string, key: string): void {
    // filter out notification
    const notifications = this.getAppState(mapId).notifications.filter((item: NotificationDetailsType) => item.key !== key);
    this.getAppState(mapId).setterActions.setNotifications(notifications);
  }

  static removeAllNotifications(mapId: string): void {
    this.getAppState(mapId).setterActions.setNotifications([]);
  }

  static setAppIsCrosshairActive(mapId: string, isActive: boolean): void {
    this.getAppState(mapId).setterActions.setCrosshairActive(isActive);

    // Because the map is focused/blured, we need to enable/disable the map interaction for WCAG
    MapEventProcessor.setActiveMapInteractionWCAG(mapId, isActive);
  }

  static setDisplayLanguage(mapId: string, lang: TypeDisplayLanguage): Promise<void> {
    // Return a new promise of void when all will be done instead of promise of array of voids
    return new Promise((resolve, reject) => {
      // Change language in i18n for the useTranslation used by the ui components
      const promiseChangeLanguage = MapEventProcessor.getMapViewer(mapId).getI18nInstance().changeLanguage(lang);

      this.getAppState(mapId).setterActions.setDisplayLanguage(lang);

      // reload the basemap from new language
      const promiseResetBasemap = MapEventProcessor.resetBasemap(mapId);

      // load guide in new language
      const promiseSetGuide = AppEventProcessor.setGuide(mapId);

      // Remove all previous notifications to ensure there is no mix en and fr
      AppEventProcessor.removeAllNotifications(mapId);

      // When all promises are done
      Promise.all([promiseChangeLanguage, promiseResetBasemap, promiseSetGuide])
        .then(() => {
          // Now resolve
          resolve();
        })
        .catch((error: unknown) => {
          // Reject
          reject(formatError(error));
        });
    });
  }

  static setDisplayDateTimezone(mapId: string, displayDateTimezone: TimeIANA): void {
    this.getAppState(mapId).setterActions.setDisplayDateTimezone(displayDateTimezone);
  }

  static setDisplayTheme(mapId: string, theme: TypeDisplayTheme): void {
    this.getAppState(mapId).setterActions.setDisplayTheme(theme);
  }

  static setFullscreen(mapId: string, active: boolean, element?: TypeHTMLElement): void {
    this.getAppState(mapId).setterActions.setFullScreenActive(active);
    MapEventProcessor.getMapViewer(mapId).setFullscreen(active, element);
  }

  static setCircularProgress(mapId: string, active: boolean): void {
    this.getAppState(mapId).setterActions.setCircularProgress(active);
  }

  /**
   * Process the guide .md file and add the object to the store.
   * @param {string} mapId - ID of map to create guide object for.
   */
  static async setGuide(mapId: string): Promise<void> {
    // Start guide loading tracker
    logger.logMarkerStart('map-guide');

    const language = AppEventProcessor.getDisplayLanguage(mapId);
    const guide = await createGuideObject(mapId, language, this.getAppState(mapId).geoviewAssetsURL);
    if (guide !== undefined) this.getAppState(mapId).setterActions.setGuide(guide);

    // Check guide loading tracker
    logger.logMarkerCheck('map-guide', 'for guide to be loaded');
  }

  // #endregion

  // **********************************************************
  // Static functions for Store Map State to action on API
  // **********************************************************
  // GV NEVER add a store action who does set state AND map action at a same time.
  // GV Review the action in store state to make sure
}
